import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", slug: "", excerpt: "", image: "", content: "", published: true };

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const PostForm = ({ post, onClose, onSaved }) => {
  const [form, setForm] = useState(post ? { ...EMPTY, ...post } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)] transition-all";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug.trim() || slugify(form.title),
        excerpt: form.excerpt,
        image: form.image,
        content: form.content,
        published: !!form.published,
      };
      if (post) await axios.put(`${API}/posts/${post.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/posts`, payload, { withCredentials: true });
      toast.success(post ? "Article updated." : "Article published.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the article.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      data-testid="post-form-overlay"
    >
      <div className="absolute inset-0 bg-[#0b132b]/50 backdrop-blur-sm" onClick={onClose} />
      <motion.form
        onSubmit={submit}
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 space-y-4 shadow-2xl"
        data-testid="post-form"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-[#0b132b]">{post ? "Edit Article" : "New Article"}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors" data-testid="post-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input data-testid="post-form-title" value={form.title} onChange={set("title")} placeholder="Article title *" className={inputCls} />
        <input data-testid="post-form-slug" value={form.slug} onChange={set("slug")} placeholder="URL slug (auto from title if empty)" className={inputCls} />
        <textarea data-testid="post-form-excerpt" rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Excerpt — shown on cards and used as the search description" className={`${inputCls} resize-none`} />
        <input data-testid="post-form-image" value={form.image} onChange={set("image")} placeholder="Featured image URL (upload in Media, paste here)" className={inputCls} />
        <textarea
          data-testid="post-form-content"
          rows={12}
          value={form.content}
          onChange={set("content")}
          placeholder={"Article body. Use a line starting with ## for section headings.\n\nExample:\n## Best time to visit\nThe Engadin shines from..."}
          className={`${inputCls} resize-y font-mono text-[13px]`}
        />
        <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-600 cursor-pointer">
          <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[var(--brand)]" data-testid="post-form-published" />
          Published (visible on the website)
        </label>
        <button
          data-testid="post-form-submit"
          disabled={busy}
          className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : post ? "Save Changes" : "Publish Article"}
        </button>
      </motion.form>
    </motion.div>
  );
};

export const BlogTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/posts/all`, { withCredentials: true });
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (post) => {
    try {
      await axios.put(
        `${API}/posts/${post.id}`,
        { title: post.title, slug: post.slug, excerpt: post.excerpt || "", image: post.image || "", content: post.content, published: !post.published },
        { withCredentials: true }
      );
      toast.success(post.published ? "Article unpublished." : "Article published.");
      load();
    } catch {
      toast.error("Could not update the article.");
    }
  };

  const del = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/posts/${post.id}`, { withCredentials: true });
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Could not delete the article.");
    }
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-slate-500">{items ? items.length : 0} articles — published ones appear at /blog</p>
        <button
          data-testid="blog-add-button"
          onClick={() => setEditing(null)}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
        >
          <Plus className="w-4 h-4" /> New Article
        </button>
      </div>

      {!items ? (
        <div className="py-16 text-center" data-testid="blog-loading">
          <div className="w-10 h-10 mx-auto rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-14 text-center" data-testid="blog-empty-admin">
          <p className="font-display font-bold text-[#0b132b]">No articles yet</p>
          <p className="mt-1 text-sm text-slate-400">Publish your first travel guide — it appears at /blog immediately.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list">
          {items.map((p) => (
            <div key={p.id} className="rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid={`blog-row-${p.slug}`}>
              {p.image && <img src={p.image} alt={p.title} className="w-full h-32 object-cover" />}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-base text-[#0b132b] leading-tight">{p.title}</h3>
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${p.published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {p.published ? "Live" : "Draft"}
                  </span>
                </div>
                <p className="mt-1.5 text-xs text-slate-500 line-clamp-2">{p.excerpt}</p>
                <div className="mt-4 flex items-center gap-2">
                  <button data-testid={`blog-edit-${p.slug}`} onClick={() => setEditing(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button data-testid={`blog-toggle-${p.slug}`} onClick={() => togglePublish(p)} className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors" aria-label={p.published ? "Unpublish" : "Publish"}>
                    {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button data-testid={`blog-delete-${p.slug}`} onClick={() => del(p)} className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing !== undefined && (
          <PostForm
            post={editing}
            onClose={() => setEditing(undefined)}
            onSaved={() => {
              setEditing(undefined);
              load();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
